import React, { useState, useMemo, useEffect } from "react";
import { Card, Table, Row, Col, Form, InputGroup, Button, Pagination, Dropdown, Alert, Spinner, Badge } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import jsPDF from "jspdf";
import * as XLSX from "xlsx";
import Footer from "../layouts/Footer";
import axios from "axios";

// Configuration de l'URL de base de l'API
const API_BASE_URL = process.env.REACT_APP_API_URL;

// Configuration du client API axios
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

// Intercepteur pour ajouter automatiquement le token d'authentification
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Intercepteur pour gérer les erreurs globales
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    console.error('❌ Erreur API:', error);
    if (error.response?.status === 401) {
      localStorage.removeItem('authToken');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export default function PaymentHistory() {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Récupération du paramètre entite_id depuis l'URL
  const queryParams = new URLSearchParams(location.search);
  const entiteId = queryParams.get('entite_id');
  
  const [filterText, setFilterText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [sortField, setSortField] = useState('country');
  const [sortDirection, setSortDirection] = useState('asc');
  const [selectedTarifs, setSelectedTarifs] = useState([]);
  const [exportLoading, setExportLoading] = useState(false);
  const [tarifs, setTarifs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [refreshLoading, setRefreshLoading] = useState(false);
  const [entiteInfo, setEntiteInfo] = useState(null);

  // Fonction pour récupérer les informations de l'entité


  // Récupération des tarifs avec filtrage par entite_id
  const fetchTarifs = async (showLoader = false) => {
    try {
      if (showLoader) {
        setRefreshLoading(true);
      } else {
        setLoading(true);
      }
      setError(null);
      
      
      // Construction de l'URL avec le paramètre entite_id si présent
      let url = '/pricing/list';
      if (entiteId) {
        url = `/pricing/list?entite_id=${entiteId}`;
      }
      
      const response = await apiClient.get(url);

      const result = response.data;
      
      if (result.code === 200 && result.data) {
        setTarifs(result.data);

        const tempBusinessName = sessionStorage.getItem('temp_business_name');
  if (tempBusinessName && !entiteInfo) {
    setEntiteInfo({
      business_name: tempBusinessName,
      business_email: 'Chargement...',
      phone: null
    });
    // Nettoyer après utilisation
    sessionStorage.removeItem('temp_business_name');
  }

        
        // Si on a un entite_id, on récupère aussi les informations de l'entité
      
      } else {
        throw new Error(result.message || 'Erreur lors de la récupération des données');
      }
    } catch (err) {
      console.error('❌ Erreur API:', err.message);
      const errorMessage = err.response?.data?.message || err.message || 'Erreur de connexion au serveur';
      setError(errorMessage);
    } finally {
      setLoading(false);
      setRefreshLoading(false);
    }
  };

  // Chargement initial des données
  useEffect(() => {
    fetchTarifs();
  }, [entiteId]); // Recharger quand entiteId change

  // Fonction pour formater les données des tarifs
  const formatTarifData = (tarif) => ({
    id: tarif.id,
    dateCreation: new Date().toLocaleDateString('fr-FR'),
    type: "SMS",
    reference: `TARIF-${tarif.country}-${tarif.network}`,
    statut: "Actif",
    montant: `${tarif.sms_pricing} ${tarif.currency}`,
    // Données originales de l'API
    sms_pricing: tarif.sms_pricing,
    dev_price: tarif.dev_price,
    country: tarif.country,
    network: tarif.network,
    currency: tarif.currency,
    business_id: tarif.business_id,
    entity_name: tarif.entity_name || 'N/A'
  });

  // Filtrage et tri des données
  const filteredAndSortedItems = useMemo(() => {
    if (!tarifs || tarifs.length === 0) return [];

    const formattedTarifs = tarifs.map(formatTarifData);

    let filtered = formattedTarifs.filter(item =>
      item.dateCreation.toLowerCase().includes(filterText.toLowerCase()) ||
      item.type.toLowerCase().includes(filterText.toLowerCase()) ||
      item.reference.toLowerCase().includes(filterText.toLowerCase()) ||
      item.statut.toLowerCase().includes(filterText.toLowerCase()) ||
      item.montant.toLowerCase().includes(filterText.toLowerCase()) ||
      item.country.toLowerCase().includes(filterText.toLowerCase()) ||
      item.network.toLowerCase().includes(filterText.toLowerCase()) ||
      item.entity_name.toLowerCase().includes(filterText.toLowerCase())
    );

    // Tri
    filtered.sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];
      
      // Conversion des dates pour le tri
      if (sortField === 'dateCreation') {
        aValue = new Date(aValue.split('/').reverse().join('-'));
        bValue = new Date(bValue.split('/').reverse().join('-'));
      }
      
      // Tri numérique pour les prix SMS
      if (sortField === 'sms_pricing') {
        aValue = parseFloat(aValue);
        bValue = parseFloat(bValue);
      }
      
      if (sortDirection === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    return filtered;
  }, [tarifs, filterText, sortField, sortDirection]);

  // Pagination
  const totalPages = Math.ceil(filteredAndSortedItems.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentItems = filteredAndSortedItems.slice(startIndex, startIndex + itemsPerPage);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Gestion des sélections
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedTarifs(currentItems.map(tarif => tarif.id));
    } else {
      setSelectedTarifs([]);
    }
  };

  const handleSelectTarif = (tarifId) => {
    setSelectedTarifs(prev => 
      prev.includes(tarifId) 
        ? prev.filter(id => id !== tarifId)
        : [...prev, tarifId]
    );
  };

  const isAllSelected = currentItems.length > 0 && selectedTarifs.length === currentItems.length;
  const isSomeSelected = selectedTarifs.length > 0 && selectedTarifs.length < currentItems.length;

  // Fonction de rafraîchissement manuel
  const handleRefresh = () => {
    setLoading(true);
    fetchTarifs(true);
  };

  // Fonction pour revenir à la liste des entités
  const handleBackToEntities = () => {
    navigate('/apps/entites');
  };

  // Fonction pour voir tous les tarifs (sans filtre par entité)
  const handleViewAllTarifs = () => {
    navigate('/apps/Tarifs');
  };

  // --- Fonctions d'export ---
  const handleExport = (format) => {
    setExportLoading(true);
    const dataToExport = selectedTarifs.length > 0
      ? tarifs.filter(tarif => selectedTarifs.includes(tarif.id)).map(formatTarifData)
      : filteredAndSortedItems;

    if (!dataToExport.length) { 
      alert("Aucune donnée à exporter"); 
      setExportLoading(false); 
      return; 
    }

    // Ajouter l'information de l'entité dans l'export si disponible
    const exportMetadata = entiteInfo ? ` - ${entiteInfo.business_name}` : '';
    
    if (format === "csv") {
      const headers = ["Date Création","Type","Référence","Statut","Montant","Pays","Réseau","Prix SMS","Devise","Entité"];
      const csvContent = [
        "\uFEFF" + headers.join(";"),
        ...dataToExport.map(p => [
          p.dateCreation,
          p.type,
          p.reference,
          p.statut,
          p.montant,
          p.country,
          p.network,
          p.sms_pricing,
          p.currency,
          p.entity_name
        ].map(v=>`"${v}"`).join(";"))
      ].join("\n");
      downloadFile(csvContent, "text/csv;charset=utf-8;", `export-tarifs${exportMetadata}-${Date.now()}.csv`);

    } else if (format === "excel") {
      const ws = XLSX.utils.json_to_sheet(dataToExport.map(p => ({
        "Date Création": p.dateCreation,
        "Type": p.type,
        "Référence": p.reference,
        "Statut": p.statut,
        "Montant": p.montant,
        "Pays": p.country,
        "Réseau": p.network,
        "Prix SMS": p.sms_pricing,
        "Devise": p.currency,
        "Entité": p.entity_name
      })));
      
      const columnWidths = [
        { wch: 15 }, { wch: 10 }, { wch: 20 }, { wch: 12 }, 
        { wch: 15 }, { wch: 10 }, { wch: 12 }, { wch: 12 }, 
        { wch: 10 }, { wch: 20 }
      ];
      ws['!cols'] = columnWidths;
      
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Tarifs SMS");
      XLSX.writeFile(wb, `export-tarifs${exportMetadata}-${Date.now()}.xlsx`);

    } else if (format === "json") {
      downloadFile(JSON.stringify(dataToExport, null, 2), "application/json;charset=utf-8;", `export-tarifs${exportMetadata}-${Date.now()}.json`);

    } else if (format === "pdf") {
      generatePDF(dataToExport);
    }

    setExportLoading(false);
  };

  // Fonction pour générer PDF
  const generatePDF = (data) => {
    const doc = new jsPDF();
    
    const primaryColor = [41, 128, 185];
    const headerColor = [240, 240, 240];
    const alternateColor = [248, 249, 250];
    
    // En-tête principal avec nom de l'entité si disponible
    doc.setFillColor(...primaryColor);
    doc.rect(0, 0, 210, entiteInfo ? 30 : 25, 'F');
    
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text("TARIFS DES SERVICES SMS", 105, 15, { align: 'center' });
    
    // Afficher le nom de l'entité si disponible
    if (entiteInfo) {
      doc.setFontSize(12);
      doc.setFont("helvetica", "normal");
      doc.text(`Pour: ${entiteInfo.business_name}`, 105, 22, { align: 'center' });
    }
    
    doc.setFontSize(9);
    doc.text(`Export généré le: ${new Date().toLocaleDateString('fr-FR', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })}`, 15, entiteInfo ? 28 : 22);
    doc.text(`Total: ${data.length} tarif(s)`, 180, entiteInfo ? 28 : 22, { align: 'right' });
    
    // Configuration du tableau
    const pageWidth = 210;
    const margin = 10;
    const tableTop = entiteInfo ? 40 : 35;
    let yPosition = tableTop;
    
    const colWidths = [15, 10, 20, 12, 15, 10, 12, 12, 10, 15];
    const headers = ["DATE", "TYPE", "RÉFÉRENCE", "STATUT", "MONTANT", "PAYS", "RÉSEAU", "PRIX SMS", "DEVISE", "ENTITÉ"];
    
    // En-têtes du tableau
    doc.setFillColor(...headerColor);
    doc.rect(margin, yPosition - 4, pageWidth - 2 * margin, 8, 'F');
    
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(8);
    doc.setFont("helvetica", "bold");
    
    let xPosition = margin;
    headers.forEach((header, index) => {
      doc.text(header, xPosition + 2, yPosition);
      xPosition += colWidths[index];
    });
    
    yPosition += 6;
    
    // Données du tableau
    doc.setFontSize(7);
    doc.setFont("helvetica", "normal");
    
    data.forEach((item, index) => {
      if (yPosition > 270) {
        doc.addPage();
        yPosition = 20;
        
        doc.setFillColor(...headerColor);
        doc.rect(margin, yPosition - 4, pageWidth - 2 * margin, 8, 'F');
        
        doc.setTextColor(0, 0, 0);
        doc.setFontSize(8);
        doc.setFont("helvetica", "bold");
        
        let newX = margin;
        headers.forEach((header, idx) => {
          doc.text(header, newX + 2, yPosition);
          newX += colWidths[idx];
        });
        
        yPosition += 6;
        doc.setFontSize(7);
        doc.setFont("helvetica", "normal");
      }
      
      if (index % 2 === 0) {
        doc.setFillColor(...alternateColor);
        doc.rect(margin, yPosition - 4, pageWidth - 2 * margin, 6, 'F');
      }
      
      doc.setTextColor(0, 0, 0);
      
      const rowData = [
        item.dateCreation,
        item.type,
        item.reference,
        item.statut,
        item.montant,
        item.country,
        item.network,
        item.sms_pricing.toString(),
        item.currency,
        item.entity_name
      ];
      
      let currentX = margin;
      rowData.forEach((cell, cellIndex) => {
        if (cellIndex === 3) {
          const status = item.statut;
          if (status === "Actif") {
            doc.setTextColor(39, 174, 96);
          } else if (status === "Inactif") {
            doc.setTextColor(231, 76, 60);
          } else {
            doc.setTextColor(52, 152, 219);
          }
        } else if (cellIndex === 4) {
          doc.setTextColor(41, 128, 185);
        } else {
          doc.setTextColor(0, 0, 0);
        }
        
        doc.text(cell, currentX + 2, yPosition);
        currentX += colWidths[cellIndex];
      });
      
      yPosition += 6;
    });
    
    // Pied de page
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      
      doc.setDrawColor(200, 200, 200);
      doc.line(margin, 275, pageWidth - margin, 275);
      
      doc.setTextColor(100, 100, 100);
      doc.setFontSize(8);
      doc.setFont("helvetica", "normal");
      doc.text(`Page ${i} sur ${pageCount}`, 105, 282, { align: 'center' });
      
      doc.text("Système de Gestion des Tarifs", margin, 282);
      doc.text(new Date().getFullYear().toString(), pageWidth - margin, 282, { align: 'right' });
    }
    
    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
    const filename = entiteInfo 
      ? `tarifs-${entiteInfo.business_name.replace(/\s+/g, '-')}-${timestamp}.pdf`
      : `tarifs-sms-${timestamp}.pdf`;
    doc.save(filename);
  };

  const downloadFile = (content, type, filename) => {
    const blob = new Blob([content], { type });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    setTimeout(() => URL.revokeObjectURL(link.href), 100);
  };

  const SortableHeader = ({ field, children }) => (
    <th 
      style={{ cursor: 'pointer', userSelect: 'none' }}
      onClick={() => handleSort(field)}
      className="position-relative"
    >
      {children}
      {sortField === field && (
        <i 
          className={`bi bi-caret-${sortDirection === 'asc' ? 'up' : 'down'}-fill position-absolute ms-1`}
          style={{ fontSize: '12px' }}
        ></i>
      )}
    </th>
  );

  return (
    <div className="main main-app p-3 p-lg-4">
      <div className="container-fluid">
        {/* En-tête avec informations de l'entité */}
        <Row className="mb-4">
          <Col md={12}>
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <div className="d-flex align-items-center mb-2">
                  <Button 
                    variant="outline-secondary" 
                    size="sm" 
                    className="me-3"
                    onClick={handleBackToEntities}
                  >
                    <i className="bi bi-arrow-left me-2"></i>
                    Retour
                  </Button>
                  <h4 className="mb-0 fw-bold">
                    {entiteInfo ? `Tarifs de ${entiteInfo.business_name}` : 'Nos tarifs'}
                  </h4>
                </div>
                <p className="text-muted mb-0">
                  {entiteInfo 
                    ? `Tarification SMS pour l'entité ${entiteInfo.business_name}`
                    : 'Consultation de nos différents tarifs SMS'
                  }
                </p>
                
                {entiteInfo && (
                  <div className="mt-2">
                    <Badge bg="info" className="me-2">
                      <i className="bi bi-building me-1"></i>
                      {entiteInfo.business_name}
                    </Badge>
                    <Badge bg="light" text="dark" className="me-2">
                      <i className="bi bi-envelope me-1"></i>
                      {entiteInfo.business_email}
                    </Badge>
                    {entiteInfo.phone && (
                      <Badge bg="light" text="dark">
                        <i className="bi bi-telephone me-1"></i>
                        {entiteInfo.phone}
                      </Badge>
                    )}
                  </div>
                )}
              </div>
              <div className="d-flex gap-2">
                {entiteId && (
                  <Button 
                    variant="outline-primary" 
                    onClick={handleViewAllTarifs}
                    className="me-2"
                  >
                    <i className="bi bi-eye me-2"></i>
                    Voir tous les tarifs
                  </Button>
                )}
                <Dropdown>
                  <Dropdown.Toggle 
                    variant="outline-primary" 
                    disabled={exportLoading}
                  >
                    <i className={`bi ${exportLoading ? 'bi-arrow-repeat spin' : 'bi-download'} me-2`}></i>
                    {exportLoading ? 'Export...' : 'Exporter'}
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    <Dropdown.Item onClick={() => handleExport('csv')}>
                      <i className="bi bi-file-earmark-spreadsheet me-2"></i>
                      Export CSV
                    </Dropdown.Item>
                    <Dropdown.Item onClick={() => handleExport('excel')}>
                      <i className="bi bi-file-earmark-excel me-2"></i>
                      Export Excel
                    </Dropdown.Item>
                    <Dropdown.Item onClick={() => handleExport('pdf')}>
                      <i className="bi bi-file-earmark-pdf me-2"></i>
                      Export PDF
                    </Dropdown.Item>
                    <Dropdown.Item onClick={() => handleExport('json')}>
                      <i className="bi bi-file-earmark-code me-2"></i>
                      Export JSON
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              </div>
            </div>
          </Col>
        </Row>

        {error && (
          <Row className="mb-3">
            <Col md={12}>
              <Alert variant="danger" className="py-2">
                <div className="d-flex justify-content-between align-items-center">
                  <span>
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    {error}
                  </span>
                  <Button variant="outline-danger" size="sm" onClick={handleRefresh}>
                    Réessayer
                  </Button>
                </div>
              </Alert>
            </Col>
          </Row>
        )}

        <Row>
          <Col md={12}>
            <Card className="shadow-sm border-0">
              <Card.Header className="bg-primary text-white d-flex justify-content-between align-items-center py-3">
                <div className="d-flex align-items-center">
                  <i className="bi bi-currency-exchange me-2 fs-5"></i>
                  <span className="fw-semibold">
                    {entiteInfo 
                      ? `Tarifs SMS pour ${entiteInfo.business_name}`
                      : 'Tableau des tarifs SMS'
                    }
                  </span>
                </div>
                <div className="text-white-50 small d-flex align-items-center">
                  {loading ? (
                    <Spinner animation="border" size="sm" variant="light" />
                  ) : (
                    <>
                      <i className="bi bi-info-circle me-1"></i>
                      <span>{filteredAndSortedItems.length} tarif(s)</span>
                    </>
                  )}
                </div>
              </Card.Header>
              
              <Card.Body className="p-0">
                {/* Barre d'outils */}
                <div className="p-3 border-bottom bg-light">
                  <Row className="align-items-center">
                    <Col md={6}>
                      <InputGroup>
                        <InputGroup.Text className="bg-white">
                          <i className="bi bi-search text-muted"></i>
                        </InputGroup.Text>
                        <Form.Control
                          type="text"
                          placeholder={entiteInfo 
                            ? `Rechercher dans les tarifs de ${entiteInfo.business_name}...`
                            : "Rechercher dans les tarifs..."
                          }
                          value={filterText}
                          onChange={e => setFilterText(e.target.value)}
                          className="border-start-0"
                        />
                        {filterText && (
                          <Button 
                            variant="outline-secondary" 
                            onClick={() => setFilterText('')}
                          >
                            <i className="bi bi-x"></i>
                          </Button>
                        )}
                      </InputGroup>
                    </Col>
                    <Col md={6} className="text-end">
                      <div className="d-flex align-items-center justify-content-end">
                        <Button 
                          variant="outline-secondary" 
                          size="sm" 
                          className="me-2"
                          onClick={handleRefresh}
                          disabled={refreshLoading}
                        >
                          <i className={`bi ${refreshLoading ? 'bi-arrow-repeat spin' : 'bi-arrow-clockwise'} me-2`}></i>
                          {refreshLoading ? 'Actualisation...' : 'Actualiser'}
                        </Button>
                        <Dropdown>
                          <Dropdown.Toggle variant="outline-secondary" size="sm">
                            <i className="bi bi-list-ul me-2"></i>
                            {itemsPerPage} par page
                          </Dropdown.Toggle>
                          <Dropdown.Menu>
                            <Dropdown.Item onClick={() => setItemsPerPage(5)}>5 par page</Dropdown.Item>
                            <Dropdown.Item onClick={() => setItemsPerPage(10)}>10 par page</Dropdown.Item>
                            <Dropdown.Item onClick={() => setItemsPerPage(25)}>25 par page</Dropdown.Item>
                          </Dropdown.Menu>
                        </Dropdown>
                      </div>
                    </Col>
                  </Row>
                </div>

                {/* Tableau */}
                <div className="table-responsive">
                  <Table hover className="mb-0 table-striped">
                    <thead className="bg-secondary text-white">
                      <tr>
                        <SortableHeader field="reference">RÉFÉRENCE</SortableHeader>
                        <SortableHeader field="type">TYPE</SortableHeader>
                        <SortableHeader field="country">PAYS</SortableHeader>
                        <SortableHeader field="network">RÉSEAU</SortableHeader>
                        <SortableHeader field="sms_pricing">PRIX SMS</SortableHeader>
                        <SortableHeader field="currency">DEVISE</SortableHeader>
                        <SortableHeader field="statut">STATUT</SortableHeader>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan="7" className="text-center py-5">
                            <Spinner animation="border" variant="primary" className="mb-3" />
                            <p className="mt-3 text-muted mb-0">
                              {entiteInfo 
                                ? `Chargement des tarifs pour ${entiteInfo.business_name}...`
                                : 'Chargement des tarifs...'
                              }
                            </p>
                          </td>
                        </tr>
                      ) : currentItems.length > 0 ? (
                        currentItems.map((tarif) => (
                          <tr key={tarif.id} className="align-middle">
                            <td>
                              <code>{tarif.reference}</code>
                            </td>
                            <td>
                              <span className="badge bg-info text-white">{tarif.type}</span>
                            </td>
                            <td>
                              <span className="badge bg-light text-dark border">
                                {tarif.country}
                              </span>
                            </td>
                            <td>
                              <span className="badge bg-secondary text-white">
                                {tarif.network}
                              </span>
                            </td>
                            <td>
                              <strong className="text-primary">
                                {tarif.sms_pricing}
                              </strong>
                            </td>
                            <td>
                              <small className="text-muted">{tarif.currency}</small>
                            </td>
                            <td>
                              <span className="badge bg-success">
                                {tarif.statut}
                              </span>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="7" className="text-center py-4">
                            <i className="bi bi-currency-exchange display-4 text-muted"></i>
                            <p className="mt-3 text-muted">
                              {entiteInfo 
                                ? `Aucun tarif trouvé pour ${entiteInfo.business_name}`
                                : 'Aucun tarif trouvé'
                              }
                            </p>
                            {filterText && (
                              <Button 
                                variant="outline-primary" 
                                size="sm"
                                onClick={() => setFilterText('')}
                              >
                                Réinitialiser la recherche
                              </Button>
                            )}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>

                {/* Pagination */}
                {!loading && totalPages > 1 && (
                  <div className="p-3 border-top d-flex justify-content-between align-items-center">
                    <div className="text-muted small">
                      Affichage de {startIndex + 1} à {Math.min(startIndex + itemsPerPage, filteredAndSortedItems.length)} sur {filteredAndSortedItems.length} tarifs
                    </div>
                    <Pagination className="mb-0">
                      <Pagination.Prev 
                        disabled={currentPage === 1}
                        onClick={() => setCurrentPage(currentPage - 1)}
                      />
                      {[...Array(totalPages)].map((_, index) => (
                        <Pagination.Item
                          key={index + 1}
                          active={index + 1 === currentPage}
                          onClick={() => setCurrentPage(index + 1)}
                        >
                          {index + 1}
                        </Pagination.Item>
                      ))}
                      <Pagination.Next 
                        disabled={currentPage === totalPages}
                        onClick={() => setCurrentPage(currentPage + 1)}
                      />
                    </Pagination>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>

      <style>{`
        .spin {
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
      <Footer />
    </div>
  );
}